import { Button, Flex, Group, Modal, Pagination } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Outlet } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetAiBridgeList } from '@/queries/aiBridgeQueries'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { FixedPageTitle } from '../layout/PageWithFixedToolbar/FixedPageTitle'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { AddAiBridgeHandler } from './AddAiBridgeHandler'
import { AiBridgeList } from './AiBridgeList/AiBridgeList'

const PAGE_SIZE = 10

export const AiBridgeListPage = () => {
  const [canCreateAiBridge] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Create
  ])

  const [
    isAddAiBridgeModalOpened,
    { open: openAddAiBridgeModal, close: closeAddAiBridgeModal }
  ] = useDisclosure(false)

  const [activePage, setActivePage] = useState(1)

  // The bridge viewset declares no filter backends, so the list takes pagination only.
  const {
    data: aiBridgeData,
    isFetching,
    isLoading,
    isError,
    refetch
  } = useGetAiBridgeList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE
    },
    {
      keepPreviousData: true
    }
  )

  const { data: deviceData, isLoading: isLoadingDevices } = useGetDeviceList({
    limit: 9999, // fetch all devices
    ordering: buildSortParams({
      key: 'label',
      order: 'asc'
    }).ordering
  })

  const aiBridges = aiBridgeData?.results || []
  const devices = deviceData?.results || []

  const totalPageCount = getPageCount(aiBridgeData?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setActivePage(totalPageCount)
    }
  }, [activePage, totalPageCount])

  const handleAiBridgeCreated = () => {
    closeAddAiBridgeModal()
    setActivePage(1)
    void refetch()
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <FixedPageTitle
          title={<FormattedMessage id="aiBridge.list.title" />}
          leftSection={
            canCreateAiBridge && (
              <Group gap="xs" wrap="nowrap">
                <Button
                  size="xs"
                  miw={140}
                  // The form needs the device list, so keep it unavailable until both queries settle.
                  loading={isLoading || isLoadingDevices}
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddAiBridgeModal}
                >
                  <FormattedMessage id="aiBridge.add" />
                </Button>
              </Group>
            )
          }
        />
      }
    >
      <AiBridgeList
        aiBridges={aiBridges}
        devices={devices}
        isLoading={isLoading || isFetching}
        hasError={isError}
        onReload={() => void refetch()}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={setActivePage}
        />
      </Flex>

      <Modal
        size="xl"
        opened={isAddAiBridgeModalOpened}
        title={<FormattedMessage id="aiBridge.add" />}
        onClose={closeAddAiBridgeModal}
      >
        <AddAiBridgeHandler
          devices={devices.filter((device) => device.active)}
          onCancel={closeAddAiBridgeModal}
          onAiBridgeCreated={handleAiBridgeCreated}
        />
      </Modal>

      <Outlet />
    </PageWithFixedToolbar>
  )
}
